import { z } from 'zod';

// ============================================================================
// Teléfono en formato internacional.
//
// Vive aquí y no dentro de un `.dto.ts` porque lo comparten dos lados: el
// esquema Zod que valida el alta en el servidor y el `Validators.pattern` del
// formulario. Si cada uno llevara su propia expresión, el navegador aceptaría
// lo que el servidor rechaza (o al revés) y el error saldría al enviar, sin
// decir en qué campo.
//
// Por qué se valida el formato en vez de dejarlo como texto libre: el teléfono
// es uno de los cinco campos que dan los 10 puntos de «acceso al decisor»
// (`company-scoring.default.ts` → `decisionMakerFields`). Ese criterio solo
// mira que el campo NO esté vacío, así que un «llamar por la tarde» o un
// «0555 123» a medias puntúa igual que un número al que de verdad se puede
// llamar. Obligar el campo sin validarlo habría subido la nota de todo el mundo
// sin subir la información.
// ============================================================================

/**
 * Caracteres que la gente teclea y que no son el número: espacios, guiones,
 * puntos y paréntesis del prefijo de zona. Se quitan antes de comparar, así que
 * `+971 50 123 45 67` y `+971501234567` son el mismo teléfono.
 */
const PHONE_SEPARATORS = /[\s\-.()]/g;

/**
 * E.164: un `+`, el prefijo de país (que nunca empieza por 0) y hasta 15
 * dígitos en total. El mínimo se deja en 8 dígitos: por debajo de eso no hay
 * ningún número nacional marcable con prefijo internacional, y es justo el
 * tramo donde caen los números a medio teclear.
 *
 * El `+` es obligatorio **a propósito**. La clienta opera en Emiratos pero el
 * documento pide admitir prefijos de fuera, y sin `+` un `0501234567` es
 * ambiguo: no hay forma de saber a qué país pertenece, y guardar un número que
 * no se puede marcar desde otro país es exactamente lo que este campo intenta
 * evitar.
 */
export const INTERNATIONAL_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;

/**
 * La misma regla, escrita para un `Validators.pattern` de Angular: acepta los
 * separadores que la persona teclea, porque el navegador valida sobre lo que
 * hay en el input y no sobre lo normalizado.
 */
export const INTERNATIONAL_PHONE_INPUT_REGEX = /^\+[1-9][\d\s\-.()]{7,20}$/;

/** Quita separadores. Devuelve `null` si no queda nada. */
export function normalizePhone(raw: string | null | undefined): string | null {
	const compact = (raw ?? '').replace(PHONE_SEPARATORS, '').trim();
	return compact ? compact : null;
}

/** True si el texto, ya sin separadores, es un E.164 válido. */
export function isInternationalPhone(raw: string | null | undefined): boolean {
	const compact = normalizePhone(raw);
	return !!compact && INTERNATIONAL_PHONE_REGEX.test(compact);
}

/**
 * Campo de teléfono obligatorio. **Guarda el número normalizado**, no lo que se
 * tecleó: el mismo teléfono escrito de cinco formas distintas es cinco filas
 * distintas a la hora de detectar duplicados o de exportarlo a GHL.
 */
export const RequiredPhoneSchema = z
	.string()
	.transform((value) => normalizePhone(value) ?? '')
	.refine((value) => INTERNATIONAL_PHONE_REGEX.test(value), {
		message: 'Phone must be in international format, e.g. +971501234567',
	})
	.meta({ title: 'Phone (international format)', example: '+971501234567' });

/**
 * Campo de teléfono OPCIONAL, pero normalizado y validado si viene.
 *
 * Existe para el «Get a Quote» y para las importaciones de CSV, que a menudo no
 * traen teléfono: exigirlo ahí rompería el importador de LinkedIn y Apollo. Pero
 * cuando SÍ viene tiene que guardarse igual que en el alta pública — el mismo
 * número escrito de cinco formas es cinco filas distintas al detectar duplicados
 * o al exportarlo al CRM.
 *
 * Un teléfono mal formado se guarda como vacío en vez de rechazar el alta
 * entera: en un formulario comercial vale más un lead sin teléfono que un lead
 * perdido, y el criterio de «acceso al decisor» ya cuenta el campo vacío como lo
 * que es — un dato que falta.
 */
export const OptionalPhoneSchema = z
	.string()
	.optional()
	.nullable()
	.transform((value) => {
		const compact = normalizePhone(value);
		return compact && INTERNATIONAL_PHONE_REGEX.test(compact) ? compact : null;
	})
	.meta({ title: 'Phone (international format)', example: '+971501234567' });
